import { NavLink } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthProvider';
import styles from './Sidebar.module.scss';

interface SidebarProps {
    isMobileOpen: boolean;
    onMobileClose: () => void;
    onThemeToggle?: () => void;
    isDarkTheme?: boolean;
}

const navGroups = [
    {
        label: 'Overview',
        items: [{ path: '/dashboard', label: 'Dashboard', icon: '◫' }],
    },
    {
        label: 'My Finances',
        items: [
            { path: '/assets', label: 'Assets', icon: '🏦' },
            { path: '/cashflows', label: 'Income & Expenses', icon: '↕' },
            { path: '/goals', label: 'Goals', icon: '◎' },
        ],
    },
    {
        label: 'Planning & Tax',
        items: [
            { path: '/simulator', label: 'Simulator', icon: '📈' },
            { path: '/tax', label: 'Tax Deep-Dive', icon: '🧾' },
            { path: '/pensions', label: 'Pensions', icon: '🏛️' },
            { path: '/healthcare-bridge', label: 'Healthcare Bridge', icon: '🏥' },
        ],
    },
];

export function Sidebar({ isMobileOpen, onMobileClose, onThemeToggle, isDarkTheme }: SidebarProps) {
    const { signOut } = useAuth();

    return (
        <aside className={`${styles.sidebar} ${isMobileOpen ? styles.mobileOpen : ''}`}>
            <div className={styles.brand}>
                <div className={styles.logo}>
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="16" cy="16" r="14" stroke="#3B5BDB" strokeWidth="2.5" />
                        <path
                            d="M4 16C4 16 8 12 16 12C24 12 28 16 28 16"
                            stroke="#3B5BDB"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                        />
                        <path
                            d="M16 8L16 16L20 20"
                            stroke="#0F9B8E"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
                <div className={styles.brandText}>
                    <span className={styles.brandName}>Expat Horizon</span>
                    <span className={styles.brandSub}>Cross-Border Finance</span>
                </div>
            </div>

            <nav className={styles.nav}>
                {navGroups.map((group) => (
                    <div key={group.label} className={styles.navGroup}>
                        <span className={styles.groupLabel}>{group.label}</span>
                        <ul className={styles.navList}>
                            {group.items.map((item) => (
                                <li key={item.path}>
                                    <NavLink
                                        to={item.path}
                                        className={({ isActive }) =>
                                            `${styles.navItem} ${isActive ? styles.active : ''}`
                                        }
                                        onClick={onMobileClose}
                                    >
                                        <span className={styles.navIcon}>{item.icon}</span>
                                        <span className={styles.navLabel}>{item.label}</span>
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>

            <div className={styles.bottomSection}>
                {onThemeToggle && (
                    <div className={styles.themeToggle}>
                        <button
                            className={`${styles.themeToggleBtn} ${isDarkTheme ? styles.dark : ''}`}
                            onClick={onThemeToggle}
                            aria-label="Toggle theme"
                        >
                            <span className={styles.toggleIcon}>☀️</span>
                            <span className={styles.togglePill} />
                            <span className={styles.toggleIcon}>🌙</span>
                        </button>
                    </div>
                )}
                <ul className={styles.navList}>
                    <li>
                        <button className={styles.navItem} onClick={signOut}>
                            <span className={styles.navIcon}>⏻</span>
                            <span className={styles.navLabel}>Sign Out</span>
                        </button>
                    </li>
                </ul>
            </div>
        </aside>
    );
}
